/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/redux/store";

export const operatorsSelector = (state: RootState) => state.operator.operators;
export const activeOperatorSelector = (state: RootState) => state.operator.activeOperator;
export const operatorIdSelector = (state: any) => state.operator.activeOperator?.id;
export const defaultCountrySelector = (state: any) => state.operator.activeOperator?.country;

const normaliseCountryCodes = (codes: Array<string | undefined>): string[] =>
  Array.from(new Set(codes.map((code) => (code ?? "").trim().toLowerCase()).filter((code) => code.length > 0)));

/**
 * The countries the provider serves, for address search: `bookable_country_list` (the domain's
 * `d-operates-in-countries`, e.g. AU and NZ for Royale), lower-cased and deduped, falling back to the base
 * `country` when the list is empty. Empty only when the provider has neither, in which case the address
 * search offers nothing. Never truncated — Google's five-countries-per-request limit is handled by fanning
 * out requests (`getPlacePredictionsForCountries`, DEV-1612). Memoised on the active operator so it is
 * stable in effect dependencies (DEV-1606).
 *
 * Every tier reads this list. The one exception to "restricted" is an operator booking on behalf whose
 * provider has the Worldwide Address Search add-on — see useAddressSearchCountries.
 */
export const bookableCountriesSelector = createSelector([activeOperatorSelector], (operator): string[] => {
  const list = normaliseCountryCodes(operator?.bookableCountryList ?? []);
  return list.length ? list : normaliseCountryCodes([operator?.country]);
});

/**
 * The operator's configured courtesy waiting minutes, or undefined when the payload doesn't state them.
 *
 * Undefined is a real answer and consumers must render nothing rather than substitute 60/30/10 — the
 * defaults are what the booking sheet and the close-job form used to hardcode, which misquoted every
 * operator who had changed them. See `Operator.waitingTimes`.
 */
export const waitingTimesSelector = (state: RootState) => state.operator.activeOperator?.waitingTimes;
